// pages/help/Help.jsx
import React, { useEffect } from "react";
import { useLocation, useParams, matchPath } from "react-router-dom";
import { COMPLETE_HELP_ROUTES } from "../../routes/routes.config";
import PropTypes from "prop-types";
import "../../assets/scss/views/Help.scss";
import { useLayout } from "../../context/LayoutContext";
import { useHelp } from "../../context/HelpContext";

// Proveedores de contexto
import { TicketsProvider } from "../../context/TicketsContext";

// Importación de vistas de Help
import DashboardHelp from "./views_help/DashboardHelp";
import ViewTickets from "./views_help/ViewTickets";
import ViewAPI from "./views_help/ViewAPI";
import ViewDatabase from "./views_help/ViewDatabase";
import ViewBackend from "./views_help/ViewBackend";
import ViewFrontend from "./views_help/ViewFrontend";
import ViewAccessibility from "./views_help/ViewAccessibility";
import ViewDataProcessing from "./views_help/ViewDataProcessing";
import ViewTermsConditions from "./views_help/ViewTermsConditions";
import ViewInformationProtection from "./views_help/ViewInformationProtection";
import ViewContact from "./views_help/ViewContact";

// Importación de vistas de documentación específica
import ViewBackendIndexJs from "./views_help/ViewBackendIndexJs";
import ViewBackendWhatsappManager from "./views_help/ViewBackendWhatsappManager";

// Importación del componente LeftNav
import HelpLeftNav from "../../components/layout/HelpLeftNav";

export default function Help({ routeConfig }) {
  const location = useLocation();
  const params = useParams();
  const { setHeaderTitle, theme, isNavExpanded, toggleNav } = useLayout();
  const { ui: { leftNavExpanded }, toggleLeftNav } = useHelp();

  // -------------------------------------------------------------
  // Titulo del header
  // -------------------------------------------------------------
  const getHeaderTitle = () => {
    if (!routeConfig) {
      // Verificar rutas con parámetros dinámicos
      const departmentMatch = matchPath(
        { path: COMPLETE_HELP_ROUTES.ticketDepartment.path },
        location.pathname
      );

      if (departmentMatch) {
        return "Departamento de Tickets";
      }

      // Coincidencia exacta
      let currentRoute = Object.values(COMPLETE_HELP_ROUTES).find(
        (route) => route.path === location.pathname
      );

      return currentRoute?.title || "Centro de Ayuda";
    }
    return routeConfig.title;
  };

  const headerTitle = getHeaderTitle();

  // -------------------------------------------------------------
  // Contenido específico según la ruta
  // -------------------------------------------------------------
  const getRouteSpecificContent = () => {
    // Manejar rutas dinámicas
    const departmentMatch = matchPath(
      { path: COMPLETE_HELP_ROUTES.ticketDepartment.path },
      location.pathname
    );

    // Si coincide con la ruta de departamento, renderizar ViewTickets con el mismo componente
    if (departmentMatch) {
      return (
        <TicketsProvider>
          <ViewTickets departmentId={departmentMatch.params.departmentId} />
        </TicketsProvider>
      );
    }

    // Para rutas estáticas
    switch (location.pathname) {
      case COMPLETE_HELP_ROUTES.tickets.path:
        return (
          <TicketsProvider>
            <ViewTickets />
          </TicketsProvider>
        );
      case COMPLETE_HELP_ROUTES.api.path:
        return <ViewAPI />;
      case COMPLETE_HELP_ROUTES.database.path:
        return <ViewDatabase />;
      case COMPLETE_HELP_ROUTES.backend.path:
        return <ViewBackend />;
      case COMPLETE_HELP_ROUTES.frontend.path:
        return <ViewFrontend />;
      case COMPLETE_HELP_ROUTES.accessibility.path:
        return <ViewAccessibility />;
      case COMPLETE_HELP_ROUTES.dataProcessing.path:
        return <ViewDataProcessing />;
      case COMPLETE_HELP_ROUTES.termsConditions.path:
        return <ViewTermsConditions />;
      case COMPLETE_HELP_ROUTES.informationProtection.path:
        return <ViewInformationProtection />;
      case COMPLETE_HELP_ROUTES.contact.path:
        return <ViewContact />;
      
      // Rutas de documentación específica del backend
      case COMPLETE_HELP_ROUTES.backendIndexJs.path:
        return <ViewBackendIndexJs />;
      case COMPLETE_HELP_ROUTES.backendWhatsappManager.path:
        return <ViewBackendWhatsappManager />;
      
      default:
        return <DashboardHelp />;
    }
  };

  // Efecto para manejar el título del header
  useEffect(() => {
    setHeaderTitle(headerTitle);
  }, [headerTitle, setHeaderTitle]);

  // -------------------------------------------------------------
  // Render principal
  // -------------------------------------------------------------
  return (
    <div className={`GM__${theme}-help-container`}>
      {/* Left Navigation */}
      <aside className={`GM__${theme}-help-leftnav ${!leftNavExpanded ? 'expanded' : 'collapsed'}`}>
        <HelpLeftNav 
          isExpanded={!leftNavExpanded}
          currentPath={location.pathname}
          theme={theme}
        />
      </aside>

      {/* Main content area */}
      <div className={`GM__${theme}-help-main ${leftNavExpanded ? 'with-expanded-nav' : 'with-collapsed-nav'}`}>
        {/* Header */}
        <header className={`GM__${theme}-help-header`}>
          <div className={`GM__${theme}-help-header-content`}>
            <button 
              className={`GM__${theme}-help-nav-toggle`}
              onClick={toggleLeftNav}
              aria-label={leftNavExpanded ? "Contraer navegación" : "Expandir navegación"}
            >
              <svg 
                width="20" 
                height="20" 
                viewBox="0 0 24 24" 
                fill="none" 
                stroke="currentColor" 
                strokeWidth="2"
              >
                {!leftNavExpanded ? (
                  // Icono para contraer (panel left)
                  <path d="M15 18l-6-6 6-6" />
                ) : (
                  // Icono para expandir (panel right)
                  <path d="M9 18l6-6-6-6" />
                )}
              </svg>
            </button>
            
            <h1 className={`GM__${theme}-help-title`}>
              {headerTitle}
            </h1>
          </div>
          
          <div className={`GM__${theme}-help-header-divider`} />
        </header>

        {/* Content */}
        <main className={`GM__${theme}-help-content`}>
          {getRouteSpecificContent()}
        </main>
      </div>
    </div>
  );
}

Help.propTypes = {
  routeConfig: PropTypes.shape({
    path: PropTypes.string,
    title: PropTypes.string,
    subtitle: PropTypes.string,
    category: PropTypes.string,
    parent: PropTypes.string,
  }),
};